import React, { useEffect, useState } from "react";
import { X, CheckCircle, XCircle, AlertCircle, Info } from "lucide-react";

interface SnackbarProps {
  isOpen: boolean;
  onClose: () => void;
  type: "success" | "error" | "warning" | "info";
  title: string;
  message: string;
  apiMessage?: string;
  autoClose?: boolean;
  autoCloseDelay?: number;
}

const Snackbar: React.FC<SnackbarProps> = ({
  isOpen,
  onClose,
  type,
  title,
  message,
  apiMessage,
  autoClose = true,
  autoCloseDelay = 5000,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      // Small delay to ensure smooth animation
      setTimeout(() => setIsAnimating(true), 10);
    } else {
      setIsAnimating(false);
      // Wait for animation to complete before hiding
      setTimeout(() => setIsVisible(false), 300);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && autoClose) {
      const timer = setTimeout(() => {
        onClose();
      }, autoCloseDelay);

      return () => clearTimeout(timer);
    }
  }, [isOpen, autoClose, autoCloseDelay, onClose]);

  if (!isVisible) return null;

  const getIcon = () => {
    const iconClasses = "h-5 w-5 transition-all duration-300";
    switch (type) {
      case "success":
        return <CheckCircle className={`${iconClasses} text-emerald-500`} />;
      case "error":
        return <XCircle className={`${iconClasses} text-red-500`} />;
      case "warning":
        return <AlertCircle className={`${iconClasses} text-amber-500`} />;
      case "info":
        return <Info className={`${iconClasses} text-blue-500`} />;
      default:
        return <Info className={`${iconClasses} text-blue-500`} />;
    }
  };

  const getBackgroundColor = () => {
    switch (type) {
      case "success":
        return "bg-gradient-to-r from-emerald-50 to-emerald-100 border-emerald-200";
      case "error":
        return "bg-gradient-to-r from-red-50 to-red-100 border-red-200";
      case "warning":
        return "bg-gradient-to-r from-amber-50 to-amber-100 border-amber-200";
      case "info":
        return "bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200";
      default:
        return "bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200";
    }
  };

  const getTitleColor = () => {
    switch (type) {
      case "success":
        return "text-emerald-900";
      case "error":
        return "text-red-900";
      case "warning":
        return "text-amber-900";
      case "info":
        return "text-blue-900";
      default:
        return "text-blue-900";
    }
  };

  const getMessageColor = () => {
    switch (type) {
      case "success":
        return "text-emerald-800";
      case "error":
        return "text-red-800";
      case "warning":
        return "text-amber-800";
      case "info":
        return "text-blue-800";
      default:
        return "text-blue-800";
    }
  };

  const getIconBackground = () => {
    switch (type) {
      case "success":
        return "bg-emerald-100";
      case "error":
        return "bg-red-100";
      case "warning":
        return "bg-amber-100";
      case "info":
        return "bg-blue-100";
      default:
        return "bg-blue-100";
    }
  };

  return (
    <div className="fixed top-4 right-4 z-50 max-w-sm w-full">
      <div
        className={`transform transition-all duration-300 ease-in-out ${
          isAnimating
            ? "translate-x-0 opacity-100 scale-100"
            : "translate-x-full opacity-0 scale-95"
        }`}
      >
        <div
          className={`${getBackgroundColor()} border rounded-xl shadow-lg backdrop-blur-sm bg-white/95 p-4 relative overflow-hidden`}
          style={{
            boxShadow:
              "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.1)",
          }}
        >
          {/* Decorative background pattern */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute top-0 right-0 w-16 h-16 bg-current rounded-full -translate-y-8 translate-x-8"></div>
          </div>

          <div className="relative flex items-start">
            {/* Icon with background circle */}
            <div
              className={`flex-shrink-0 p-2 rounded-full ${getIconBackground()} transition-all duration-300`}
            >
              {getIcon()}
            </div>

            <div className="ml-3 flex-1 min-w-0">
              <h3 className={`text-sm font-semibold ${getTitleColor()} mb-1`}>
                {title}
              </h3>
              <div className={`text-xs leading-relaxed ${getMessageColor()}`}>
                <p className="mb-1">{message}</p>
                {apiMessage && apiMessage !== message && (
                  <div className="mt-2 p-2 bg-white/50 rounded-md border border-current/20">
                    <p className="text-xs font-medium opacity-90 mb-1">
                      API Response:
                    </p>
                    <p className="text-xs opacity-75">{apiMessage}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Close button */}
            <div className="ml-2 flex-shrink-0">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center justify-center rounded-full p-1 transition-all duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-offset-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:ring-gray-500"
              >
                <span className="sr-only">Close</span>
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Progress bar for auto-close */}
          {autoClose && (
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/10 rounded-b-xl overflow-hidden">
              <div
                className="h-full bg-current opacity-30"
                style={{
                  animation: `shrink ${autoCloseDelay}ms linear forwards`,
                  width: "100%",
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Snackbar;
